import React, { createContext, useContext, useCallback, useState, useMemo, useEffect } from 'react';
import { PROPERTY_TYPE_OPTIONS, SALE_LEASE_OPTIONS } from 'data/commercial/search';
import { SearchStrategyFactory } from 'strategy/search';
import { SEARCH_OPTION_COMMERCIAL } from 'tenants/types/theme';
import useDebounce from 'hooks/use-debounce';

import type {
  GetStatesResponse,
  GetCitiesResponse,
} from '@zoocasa/go-search';
import type SearchPrediction from 'data/search-predictions';
import { parseAsArrayOf, parseAsFloat, parseAsString, useQueryStates } from 'nuqs';

export const DEFAULT_MAP_ZOOM = 3;
export const DEFAULT_MAP_CENTER = { lat: 39.8283, lng: -98.5795 };

export type TopFilters = {
  propertyTypes: Array<string>;
  states: Array<string>;
  cities: Array<string>;
  saleLease: Array<string>;
};

interface ListingsFilterState {
  // Filters
  filters: TopFilters;

  // Available options
  filterOptions: {
    states: Array<string>;
    cities: Array<string>;
    propertyTypes: Array<string>;
    saleLease: Array<string>;
  };

  // Mappings
  statesToCodesMappings: Record<string, string>;

  // Search predictions
  isLoadingSearchPredictions: boolean;
  searchPredictions: SearchPrediction[];
  isTyping: boolean;
}

interface ListingsFilterActions {
  setFilters: (filters: Partial<TopFilters>) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  mapCenter: { lat: number; lng: number };
  mapZoom: number;
  setMapCenter: (center: { lat: number; lng: number }) => void;
  setMapZoom: (zoom: number, center?: { lat: number; lng: number }) => void;
  
  // Region is used for search predictions
  mapRegion: mapkit.CoordinateRegion | undefined;
  setMapRegion: (region: mapkit.CoordinateRegion | undefined) => void;
}

type ListingsFilterContextType = ListingsFilterState & ListingsFilterActions;

const ListingsFilterContext = createContext<ListingsFilterContextType | undefined>(undefined);

export interface ListingsFilterProviderProps {
  children: React.ReactNode;
  availableStates: GetStatesResponse['states'];
  availableCities: GetCitiesResponse['cities'];
}

export const ListingsFilterProvider: React.FC<ListingsFilterProviderProps> = ({
  children,
  availableStates,
  availableCities,
}) => {
  // Filter options (all available values)
  const filterOptions = useMemo(() => ({
    states: availableStates.map(state => state.stateName),
    cities: availableCities.map(city => city.name),
    propertyTypes: [...PROPERTY_TYPE_OPTIONS],
    saleLease: [...SALE_LEASE_OPTIONS],
  }), [availableStates, availableCities]);

  // URL state management with nuqs - combined into one hook
  const [urlFilters, setUrlFilters] = useQueryStates({
    propertyTypes: parseAsArrayOf(parseAsString).withDefault([...PROPERTY_TYPE_OPTIONS]).withOptions({ clearOnDefault: true }),
    states: parseAsArrayOf(parseAsString).withDefault(filterOptions.states),
    cities: parseAsArrayOf(parseAsString).withDefault(filterOptions.cities).withOptions({ clearOnDefault: true }),
    saleLease: parseAsArrayOf(parseAsString).withDefault([...SALE_LEASE_OPTIONS]).withOptions({ clearOnDefault: true }),
    lat: parseAsFloat.withDefault(DEFAULT_MAP_CENTER.lat),
    lng: parseAsFloat.withDefault(DEFAULT_MAP_CENTER.lng),
    zoom: parseAsFloat.withDefault(DEFAULT_MAP_ZOOM),
  });


  // Derive filters from URL state (source of truth)
  const filters = useMemo<TopFilters>(() => ({
    propertyTypes: urlFilters.propertyTypes,
    states: urlFilters.states,
    cities: urlFilters.cities,
    saleLease: urlFilters.saleLease,
  }), [urlFilters]);

  // Derive map state from URL
  const mapCenter = useMemo(() => ({ 
    lat: urlFilters.lat, 
    lng: urlFilters.lng,
  }), [urlFilters.lat, urlFilters.lng]);
  
  const mapZoom = useMemo(() => urlFilters.zoom, [urlFilters.zoom]);

  const statesToCodesMappings = useMemo(() => {
    const mappings: Record<string, string> = {};
    for (const state of availableStates) {
      mappings[state.stateName] = state.name;
    }
    return mappings;
  }, [availableStates]);

  const [searchQuery, setSearchQuery] = useState('');
  // used for state/city/zip code google suggestions
  const [mapRegion, setMapRegion] = useState<mapkit.CoordinateRegion | undefined>(undefined);

  // Search prediction state
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingSearchPredictions, setIsLoadingSearchPredictions] = useState(false);
  const [searchPredictions, setSearchPredictions] = useState<SearchPrediction[]>([]);

  // Action handlers - update URL state which drives the filters
  const setFilters = useCallback((updatedFilters: Partial<TopFilters>) => {
    const updates: Record<string, string[] | null> = {};

    // Update filters in URL
    for (const [key, value] of Object.entries(updatedFilters) as [keyof TopFilters, Array<string>][]) {
      if (filterOptions[key].length === value.length) {
        updates[key] = null; // If all values are selected, remove from URL (use default)
      } else if (value.length === 0) {
        updates[key] = [];
      } else {
        updates[key] = value;
      }
    }

    // Apply all updates at once
    if (Object.keys(updates).length > 0) {
      setUrlFilters(prev => ({ ...prev, ...updates }));
    }
  }, [filterOptions, setUrlFilters]);

  // Map state setters - update URL params
  const setMapCenter = useCallback((center: { lat: number; lng: number }) => {
    setUrlFilters(prev => ({
      ...prev,
      lat: center.lat,
      lng: center.lng,
    }));
  }, [setUrlFilters]);

  const setMapZoom = useCallback((zoom: number, center?: { lat: number; lng: number }) => {
    // Use provided center if available (from zoom event), otherwise use current URL filter values
    const centerToUse = center || { lat: urlFilters.lat, lng: urlFilters.lng };
    
    // Always include lat and lng when updating zoom to ensure they're in the URL
    // If they're at defaults, add tiny epsilon to force nuqs to serialize them
    const latToSet = centerToUse.lat === DEFAULT_MAP_CENTER.lat 
      ? DEFAULT_MAP_CENTER.lat + 0.0000001 
      : centerToUse.lat;
    const lngToSet = centerToUse.lng === DEFAULT_MAP_CENTER.lng 
      ? DEFAULT_MAP_CENTER.lng + 0.0000001 
      : centerToUse.lng;
    
    setUrlFilters(prev => ({
      ...prev,
      lat: latToSet,
      lng: lngToSet,
      zoom,
    }));
  }, [setUrlFilters, urlFilters.lat, urlFilters.lng]);

  // Search with strategy function (from useCommercialSearch hook)
  const searchWithStrategy = useCallback(async () => {
    const performSearch = searchQuery.length > 2;
    const performReset = searchQuery.length === 0;

    if (performSearch) {
      setIsLoadingSearchPredictions(true);

      try {
        // Get the commercial search strategy
        const searchStrategy = SearchStrategyFactory.getStrategy(SEARCH_OPTION_COMMERCIAL);
        
        // Execute the search using the selected strategy
        const { searchPredictions: predictions } =
          await searchStrategy.search({
            queryString: searchQuery,
            // Note: Commercial search is US-only, these parameters are ignored by CommercialSearchStrategy
            siteLocation: 'US',
            themeName: 'commercial',
          });

        setSearchPredictions(predictions);
      } catch (err: any) {
        console.error(err);
        setSearchPredictions([]);
      }

      setIsTyping(false);
      setIsLoadingSearchPredictions(false);
    } else if (performReset) {
      setIsTyping(false);
      setSearchPredictions([]);
      setIsLoadingSearchPredictions(false);
    }
  }, [searchQuery, SEARCH_OPTION_COMMERCIAL]);

  // Debounced search function
  const debouncedSearch = useCallback(useDebounce(searchWithStrategy, 400), [searchWithStrategy]);

  // Effect to handle search query changes and trigger search
  useEffect(() => {
    debouncedSearch();
    if (searchQuery.length) {
      setIsTyping(true);
    }
  }, [searchQuery, debouncedSearch]);


  const contextValue: ListingsFilterContextType = {
    // State
    filters,
    filterOptions,
    statesToCodesMappings,

    // Search predictions state
    isLoadingSearchPredictions,
    searchPredictions,
    isTyping,

    // Actions
    setFilters,
    searchQuery,
    setSearchQuery,
    
    // Map state (URL-driven)
    mapCenter,
    mapZoom,
    setMapCenter,
    setMapZoom,
    mapRegion,
    setMapRegion,
  };

  return (
    <ListingsFilterContext.Provider value={contextValue}>
      {children}
    </ListingsFilterContext.Provider>
  );
};

export const useCommercialListingsFilter = () => {
  const context = useContext(ListingsFilterContext);
  if (context === undefined) {
    console.warn('useCommercialListingsFilter must be used within a ListingsFilterProvider');
  }
  return context;
};

export default ListingsFilterContext;
